import { useState } from "react";
import libraryStyles from "../../../features/library/library.css";
import { workshopStartStyles } from "../../../features/admin/workshops/workshops-styles.css.js";
import cx from "../../../lib/cx.js";
import Icon from "../Icon/Icon.jsx";

/**
 * A React component representing a fallback UI element.
 *
 * Fallback is designed to be used as a placeholder when certain content cannot be displayed.
 * It is created using the XCol component with specific properties set for background color,
 * alignment, and CSS class styling. It also includes an IconLink component.
 *
 * Properties:
 * - bg: Specifies the background color of the component. Set to "gray900".
 * - align: Centers the content horizontally.
 * - justify: Centers the content vertically.
 * - className: Applies specific CSS styles from libraryStyles.previewElement.
 *
 * The IconLink component is included within Fallback, with its size set to "xxxl".
 */
const Fallback = (
  <div className={libraryStyles.previewElement}>
    <Icon name="open" />
  </div>
);

/**
 * A React component that renders a link preview image. It attempts to fetch an image
 * from a proxy server to display as the preview. If the image is not available or if
 * the user is not authenticated, a fallback component is rendered instead.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.imgUrl - The URL of the image to be used as the link preview.
 * @param {string} [props.alt="Linkvorschaubild"] - The alternative text for the image, displayed when the image cannot be rendered.
 *
 * @returns {JSX.Element} The JSX for the link preview or a fallback component if the image is not available.
 */
export const LinkPreview = ({ imgUrl, alt = "Linkvorschaubild" }) => {
  // `failed` tracks images the proxy can't serve (404/400/500) so we swap in the
  // fallback instead of a broken-image icon — the same behaviour the previous
  // fetch-then-set-src approach gave us.
  const [failed, setFailed] = useState(false);

  if (!imgUrl || failed) {
    return Fallback;
  }

  // We proxy the link preview image via our own (auth-protected) server.
  // `crossOrigin="use-credentials"` makes the browser send our session cookie
  // cross-origin — required because `/proxy/img` is behind `withAuth`.
  // `loading="lazy"` defers the request until the image nears the viewport, so a
  // library page with hundreds of links no longer fires hundreds of proxy calls
  // on mount. The reserved aspect ratio (via `previewElement`) lets the browser
  // correctly tell which images are off-screen.
  const proxyUrl = `${import.meta.env.VITE_API_HOST}/proxy/img?url=${encodeURIComponent(imgUrl)}`;

  return (
    <img
      src={proxyUrl}
      crossOrigin="use-credentials"
      loading="lazy"
      decoding="async"
      className={cx(workshopStartStyles.coverImg, libraryStyles.previewElement)}
      alt={alt}
      onError={() => setFailed(true)}
    />
  );
};
